import { useIsAuthenticated } from '@azure/msal-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import useIsInWebView from '~/common/hooks/useIsInWebView';

import {
  InactivityEvents,
  InactivityStorageKeys,
  inactivityDefaultTimeout,
  modalCountdownTimeout,
  WEB_VIEW_INACTIVITY_TIMEOUT,
} from '~/hooks/useInactivityTracker/InactivityTracker.constants';
import { InactivityTracker } from '~/hooks/useInactivityTracker/inactivityTracker';

const useInactivityTracker = () => {
  //Cannot use UseIsAuthenticated from MSAL, as provider is wrapped separately in the app tree
  //Need to use different hook for authentication
  const [inactivityTracker, setInactivityTracker] =
    useState<InactivityTracker | null>(null);
  const [isUserInactive, setIsUserInactive] = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const { isInWebView } = useIsInWebView();
  // Synchronous mirror of isUserInactive used by the refocus handler. State
  // updates are async, so the visibilitychange/focus pair can both observe a
  // stale `false` and fire twice; this ref is flipped synchronously to dedupe.
  const inactiveHandledRef = useRef(false);

  useEffect(() => {
    inactiveHandledRef.current = isUserInactive;
  }, [isUserInactive]);

  const handleStorageEvent = useCallback(
    (event: StorageEvent) => {
      // want to synchronize when inactivity modal pops up for all tabs so that all tabs show the modal at the same time
      if (
        event.key === InactivityStorageKeys.userInactive &&
        event.newValue !== null &&
        isUserInactive === false
      ) {
        // Flip the dedupe ref synchronously here too, so a cross-tab inactive
        // signal makes this tab's refocus handler idempotent before the async
        // isUserInactive/mirror update lands (otherwise refocus could run
        // handleInactiveUser a second time).
        inactiveHandledRef.current = true;
        setIsUserInactive(true);
        inactivityTracker?.removeTrackers();
      }
      // want to make sure that when the user is active, the inactivity tracker is reset only when the user isn't already considered inactive
      if (
        event.key === InactivityStorageKeys.userActive &&
        event.newValue !== null &&
        isUserInactive === false
      ) {
        setIsUserInactive(false);
        inactivityTracker?.resetTimer();
      }
      //need to ensure that the inactivity tracker is set up again after a user chooses to extend their session
      if (
        event.key === InactivityStorageKeys.userExtendSession &&
        event.newValue !== null
      ) {
        inactivityTracker?.tracker();
        inactivityTracker?.resetTimer();
        setIsUserInactive(false);
      }
    },
    [inactivityTracker, isUserInactive]
  );

  const handleInactiveUser = useCallback(() => {
    // Mark handled synchronously so a second trigger in the same tick (the local
    // timeout event and the refocus handler can fire back-to-back before the
    // async setIsUserInactive/mirror effect lands) can't run duplicate
    // broadcasts/teardown, regardless of which path triggers inactivity first.
    inactiveHandledRef.current = true;
    // The cross-tab broadcast is best-effort: if storage is unavailable (private
    // mode/quota/blocked) it must not stop this tab from being torn down and
    // marked inactive below.
    try {
      localStorage.setItem(InactivityStorageKeys.userInactive, 'true');
      localStorage.removeItem(InactivityStorageKeys.userInactive);
    } catch {
      // Ignore storage failures; the local teardown still runs.
    }
    //Need to make sure that a user cannot dismiss the consuming components through another tab
    //Once a user hits the inactivity timeout limit
    inactivityTracker?.removeTrackers();
    setIsUserInactive(true);
  }, [inactivityTracker]);

  const setupEventListeners = useCallback(() => {
    window.addEventListener('storage', handleStorageEvent);
    window.addEventListener(InactivityEvents.inactiveUser, handleInactiveUser);
  }, [handleStorageEvent, handleInactiveUser]);

  const resetAllTimers = () => {
    if (inactivityTracker) {
      //need to ensure that the inactivity tracker is set up again after a user chooses to extend their session
      localStorage.setItem(InactivityStorageKeys.userExtendSession, 'true');
      localStorage.removeItem(InactivityStorageKeys.userExtendSession);
      inactivityTracker.tracker();
      inactivityTracker.resetTimer();
      inactivityTracker.emitActiveUser();
      setIsUserInactive(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      // Need to set the inactivity tracker to null in the case of other tabs detecting user is no longer authenticated.
      setInactivityTracker((prevTracker) => {
        prevTracker?.removeTrackers();
        return null;
      });
      setIsUserInactive(false);
      return;
    }

    const defaultTimeout = !Number.isNaN(globalThis?.appConfig?.inactivityTimer)
      ? globalThis?.appConfig?.inactivityTimer
      : inactivityDefaultTimeout;

    const timeout = isInWebView ? WEB_VIEW_INACTIVITY_TIMEOUT : defaultTimeout;
    // Because the modal timeout is included in the total allowed timeout for inactivity
    // it is necessary for the inactivity timeout to only be equal to the difference between total timeout and modal timeout
    const inactiveTimeOut =
      timeout > modalCountdownTimeout
        ? timeout - modalCountdownTimeout
        : timeout;
    const tracker = new InactivityTracker(inactiveTimeOut);
    setInactivityTracker(tracker);
    return () => {
      tracker.removeTrackers();
    };
  }, [isAuthenticated, isInWebView]);

  useEffect(() => {
    if (!inactivityTracker) {
      return;
    }
    //Reset timer to ensure inactivity tracking starts upon page load
    inactivityTracker.resetTimer();
    setupEventListeners();

    return () => {
      window.removeEventListener('storage', handleStorageEvent);
      window.removeEventListener(
        InactivityEvents.inactiveUser,
        handleInactiveUser
      );
    };
  }, [
    inactivityTracker,
    handleInactiveUser,
    handleStorageEvent,
    setupEventListeners,
  ]);

  useEffect(() => {
    if (!inactivityTracker) {
      return;
    }
    // When a backgrounded/frozen tab is brought back to the foreground its timer
    // may have been throttled and missed the timeout. Re-check the shared
    // activity timestamp on refocus so an expired session is reflected
    // immediately instead of leaving a stale tab usable (e.g. shared machine).
    const handleReturnToTab = () => {
      // Skip when inactivity was already handled so repeated tab switches and the
      // visibilitychange/focus pair (which can fire back-to-back before state
      // updates) don't run handleInactiveUser twice. The ref is set synchronously
      // to guard the second event in the same tick.
      if (
        document.visibilityState === 'visible' &&
        !inactiveHandledRef.current &&
        inactivityTracker.isSessionIdle()
      ) {
        inactiveHandledRef.current = true;
        handleInactiveUser();
      }
    };
    document.addEventListener('visibilitychange', handleReturnToTab);
    window.addEventListener('focus', handleReturnToTab);

    return () => {
      document.removeEventListener('visibilitychange', handleReturnToTab);
      window.removeEventListener('focus', handleReturnToTab);
    };
  }, [inactivityTracker, handleInactiveUser]);

  return {
    isUserInactive,
    resetAllTimers,
    idleTimeout: inactivityTracker?.idleTimeout,
  };
};

export default useInactivityTracker;
